/**
 * 메인 "커뮤니티 인기글" — caredoc 스타일 풀폭 위젯.
 *
 * - 헤더: 타이틀 + pill 탭(상위 4-5개 보드) + 더보기
 * - 본문: 2-column 번호 리스트 (총 10건, 좌 5 / 우 5)
 * - 푸터: 페이지네이션 (이전/다음 + 현재/총)
 *
 * 영구지침: caredoc.kr 차용 1차 (2026-04-29)
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight, ChevronLeft, MessageCircle } from 'lucide-react'
import apiClient from '@/lib/api/client'
import Skeleton from '@/components/ui/Skeleton'
import {
  type PublicBoardSummary,
  type PostSummary,
  type PageResponse,
} from '@/features/community/publicApi'
import {
  HOME_COMMUNITY_BOARDS_QUERY_KEY,
  homeCommunityHotQueryKey,
  sortHomeCommunityTabs,
} from '@/features/home/widgetQueries'

const MAX_TABS = 5
const PAGE_SIZE = 10

export default function CommunityHotPosts() {
  // 쿼리 키·탭 정렬은 SSR 프리페치(entry-server.tsx)와 공유 — widgetQueries.ts
  const { data: boards } = useQuery({
    queryKey: HOME_COMMUNITY_BOARDS_QUERY_KEY,
    queryFn: async () => {
      const res = await apiClient.get('/community/boards')
      return res.data.data as PublicBoardSummary[]
    },
    staleTime: 30 * 60 * 1000,
  })

  // 글이 있는 게시판을 먼저 노출 — 빈 탭이 첫 화면을 차지해 "아직 등록된 글이 없어요"가
  // 보이는 문제를 일반화해서 해결. 동률은 displayOrder 로 정렬.
  const tabs = sortHomeCommunityTabs(boards ?? []).slice(0, MAX_TABS)
  const [activeSlug, setActiveSlug] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const currentSlug = activeSlug ?? tabs[0]?.slug ?? null

  const handleTabChange = (slug: string) => {
    setActiveSlug(slug)
    setPage(0)
  }

  const { data: posts, isLoading } = useQuery({
    queryKey: homeCommunityHotQueryKey(currentSlug, page),
    queryFn: async () => {
      if (!currentSlug) return null
      const res = await apiClient.get(
        `/community/boards/${currentSlug}/posts`,
        { params: { sort: 'popular', page, size: PAGE_SIZE } }
      )
      return res.data.data as PageResponse<PostSummary>
    },
    enabled: !!currentSlug,
    staleTime: 5 * 60 * 1000,
  })

  const items = posts?.content ?? []
  const left = items.slice(0, 5)
  const right = items.slice(5, 10)
  const totalPages = posts?.totalPages ?? 1
  const canPrev = page > 0
  const canNext = page + 1 < totalPages

  return (
    <section className="rounded-2xl border border-cb-gray-100 bg-white p-5">
      <header className="mb-4 flex items-center gap-3">
        <h2 className="flex-shrink-0 text-base font-bold text-cb-gray-900">커뮤니티</h2>

        {/* Pill 탭 */}
        <div className="scrollbar-hide flex flex-1 gap-1.5 overflow-x-auto">
          {tabs.map((b) => {
            const active = b.slug === currentSlug
            return (
              <button
                key={b.slug}
                onClick={() => handleTabChange(b.slug)}
                className={`flex-shrink-0 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                  active
                    ? 'bg-cb-primary-light text-cb-primary'
                    : 'border border-cb-gray-200 text-cb-gray-500 hover:bg-cb-gray-50'
                }`}
              >
                {b.name}
              </button>
            )
          })}
        </div>

        <Link
          to={currentSlug ? `/community/${currentSlug}` : '/community'}
          className="flex flex-shrink-0 items-center gap-0.5 text-xs font-medium text-cb-gray-500 hover:text-cb-primary"
        >
          더보기 <ChevronRight size={12} />
        </Link>
      </header>

      {isLoading || !posts ? (
        <ul className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <li key={i} className="border-b border-cb-gray-100 py-2.5">
              <Skeleton height="0.875rem" width="80%" />
            </li>
          ))}
        </ul>
      ) : items.length === 0 ? (
        <p className="py-6 text-center text-xs text-cb-gray-400">
          아직 등록된 글이 없어요
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
          <PostList posts={left} startRank={1} />
          {right.length > 0 && <PostList posts={right} startRank={6} />}
        </div>
      )}

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="mt-3 flex items-center justify-center gap-3 border-t border-cb-gray-100 pt-3">
          <button
            type="button"
            disabled={!canPrev}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            aria-label="이전"
            className="flex h-7 w-7 items-center justify-center rounded-full border border-cb-gray-200 text-cb-gray-500 hover:bg-cb-gray-50 disabled:opacity-30"
          >
            <ChevronLeft size={14} />
          </button>
          <span className="text-xs text-cb-gray-500">
            <span className="font-semibold text-cb-primary">인기</span> 더보기 {page + 1}/{totalPages}
          </span>
          <button
            type="button"
            disabled={!canNext}
            onClick={() => setPage((p) => p + 1)}
            aria-label="다음"
            className="flex h-7 w-7 items-center justify-center rounded-full border border-cb-gray-200 text-cb-gray-500 hover:bg-cb-gray-50 disabled:opacity-30"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}
    </section>
  )
}

function PostList({ posts, startRank }: { posts: PostSummary[]; startRank: number }) {
  return (
    <ul className="flex flex-col">
      {posts.map((p, i) => (
        <li key={p.id} className="border-b border-cb-gray-100 last:border-0">
          <Link
            to={`/community/posts/${p.id}`}
            className="flex items-center gap-2 py-2.5 hover:bg-cb-gray-50"
          >
            <span className="w-5 flex-shrink-0 text-center text-xs font-bold text-cb-gray-500">
              {startRank + i}
            </span>
            <span className="min-w-0 flex-1 truncate text-sm text-cb-gray-800">
              {p.title}
            </span>
            {p.commentCount > 0 && (
              <span className="flex flex-shrink-0 items-center gap-0.5 text-xs text-cb-primary">
                <MessageCircle size={11} />
                {p.commentCount}
              </span>
            )}
          </Link>
        </li>
      ))}
    </ul>
  )
}
